import i18n, { LanguageDetectorModule } from "i18next";
import { initReactI18next } from "react-i18next";

import enGB from "../locales/en-GB.json";
import { normaliseLanguageCode } from "../utils/languageUtils";
import {
  isPseudoLocaleEnabled,
  pseudoLocaliseResources,
} from "../utils/pseudoLocale";
import { readRequestedLanguage } from "../utils/requestedLanguage";

/**
 * Anything reaching i18next is normalised first (EN-5224): the mobile app can
 * send a tag such as "de-null" for a device whose language has no region, and
 * i18next would then store bundles under a tag it never resolves against.
 */
const handleLanguage = () => normaliseLanguageCode(readRequestedLanguage());

export { readRequestedLanguage };

const LanguageDetector: LanguageDetectorModule = {
  type: "languageDetector",
  detect: handleLanguage,
  init: () => {},
  cacheUserLanguage: () => {},
};

i18n
  .use(LanguageDetector)

  .use(initReactI18next)

  .init({
    // EN-5191: the portal's own UI strings, bundled with the build as the baseline the app
    // always renders from. The published copy is fetched from the API and layered on top by
    // usePortalTranslations.
    //
    // ONLY en-GB SHIPS HERE, and that is deliberate: a translated portal is a portal that
    // reached the API, so any other language resolves through fallbackLng until its rows land.
    // Do not add a second bundle — it would let the app render, say, Dutch from a stale build
    // while the published copy says something else.
    //
    // Deliberately the default "translation" namespace, which the survey and article screens
    // also write into at runtime via i18n.addResourceBundle (see translationsUtils.ts). That
    // merge is shallow, and their keys are flat `survey_fixed_translations_*` /
    // `articles_fixed_translations_*` strings while these are nested under an area, so the
    // two sets cannot collide. Moving to a second namespace would change what a bare
    // useTranslation() resolves and break those screens.
    resources: {
      // A copy, not the imported object. addResourceBundle's deep merge mutates the bundle it
      // is given, so passing the module object straight in means the published copy overwrites
      // the shipped baseline in memory and nothing can read the built-in values back (EN-5191).
      //
      // Under ?pseudo=1 the copy is transformed on the way in, so the baseline and the
      // published copy layered over it by usePortalTranslations both read as pseudo — a string
      // left in plain English is then hardcoded, with no third possibility.
      "en-GB": {
        translation: isPseudoLocaleEnabled()
          ? (pseudoLocaliseResources({ ...enGB }) as typeof enGB)
          : { ...enGB },
      },
    },
    fallbackLng: "en-GB",
    // EN-5191: the portal's keys are flat, underscore-separated paths
    // ("patientList_columns_name"), so i18next must not split them. Without this it treats
    // "." as a path separator, and a key containing one would silently resolve to nothing.
    // The survey and article bundles merged in at runtime are flat too, so this suits both.
    keySeparator: false,
    interpolation: {
      // React escapes for us; letting i18next escape as well double-encodes apostrophes.
      escapeValue: false,
    },
    react: {
      // EN-5191: the published strings arrive after first paint (usePortalTranslations adds
      // them to the store), and react-i18next binds to nothing in the store by default — so
      // without this the app would keep rendering the built-in copy until a full remount.
      bindI18nStore: "added",
    },
  });

/**
 * Re-runs language detection and switches i18next if the answer has changed.
 *
 * EN-5191: the detector runs once, when this module is first evaluated — which is the first
 * page load, i.e. the login screen. At that point nobody is signed in, AUTH_UNIT_PREFERENCES is
 * empty and the detector resolves to the fallback. Signing in writes the preferences but is a
 * client-side navigation, not a reload, so nothing re-reads them: the session stayed English
 * until something else happened to change the language or the user refreshed the page.
 *
 * AuthContext calls this as soon as it has stored the preferences. Returns the language now in
 * force, so the caller can tell whether anything moved.
 */
export async function applyStoredLanguage(): Promise<string> {
  const requested = handleLanguage();

  if (requested && requested !== i18n.language) {
    await i18n.changeLanguage(requested);
  }

  return i18n.language;
}

export default i18n;
